import type { UIMessage } from 'ai';
import { isAskUser } from '@inference.engine/agentic/human-input';
import type { PendingApproval } from '../api/pending-approvals.api';

interface ToolCallPart {
  type: string;
  toolCallId?: string;
  state?: string;
  input?: unknown;
  approval?: { id: string };
}

// eslint-disable-next-line i18next/no-literal-string -- the AI SDK's part type prefix
const TOOL_PART_PREFIX = 'tool-';

/**
 * The calls a conversation's last turn paused on, read off the messages it streamed, for an agent
 * whose host keeps no conversation to ask. Shaped as the approvals endpoint returns them, so the
 * same prompts answer either.
 */
export function pendingApprovalsOf(
  messages: readonly UIMessage[],
  agent: Pick<PendingApproval, 'agentId' | 'agentName'>,
  toolLabel: (toolName: string) => string,
): PendingApproval[] {
  const last = messages.at(-1);
  if (last?.role !== 'assistant') return [];
  return (last.parts as ToolCallPart[]).flatMap((part) => {
    if (
      part.state !== 'approval-requested' ||
      !part.approval ||
      !part.toolCallId ||
      !part.type.startsWith(TOOL_PART_PREFIX)
    ) {
      return [];
    }
    const toolName = part.type.slice(TOOL_PART_PREFIX.length);
    return [
      {
        approvalId: part.approval.id,
        toolCallId: part.toolCallId,
        toolName,
        toolLabel: toolLabel(toolName),
        ...agent,
        input: (part.input ?? {}) as PendingApproval['input'],
        // eslint-disable-next-line i18next/no-literal-string -- the endpoint's kind values
        kind: isAskUser(part) ? 'question' : 'approval',
      },
    ];
  });
}
